import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import CommuteIcon from "@mui/icons-material/Commute";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

const RULES = {
  player_unavailability: {
    initial: { type: "once", participant_id: "", start: "", end: "" },
    icon: AccessTimeIcon,
  },
  resource_preferences: {
    initial: {
      tournament_id: "",
      phase: "all",
      mode: "preferred",
      field_ids: [],
      establishment_ids: [],
    },
    icon: BusinessIcon,
  },
  club_transfers: {
    initial: { from_club_id: "", to_club_id: "", minutes: 0 },
    icon: CommuteIcon,
  },
};

export default function SchedulingPoliciesSection({
  config,
  participants,
  tournaments,
  fields,
  onChange,
  disabled,
  intl,
}) {
  const [editor, setEditor] = useState(null);
  const es = intl.locale.startsWith("es");
  const t = (spanish, english) => (es ? spanish : english);
  const clubs = useMemo(
    () => [
      ...new Map(
        fields.map((field) => [field.establishment_id, { id: field.establishment_id, name: field.establishment_name }]),
      ).values(),
    ],
    [fields],
  );

  const openEditor = (key, index = null) => {
    const source = index === null ? RULES[key].initial : (config[key] || [])[index];
    setEditor({ key, index, row: cloneRule(source) });
  };

  const commitEditor = () => {
    if (!editor) return;
    const existing = config[editor.key] || [];
    const next =
      editor.index === null
        ? [...existing, editor.row]
        : existing.map((row, index) => (index === editor.index ? editor.row : row));
    onChange(editor.key, next);
    setEditor(null);
  };

  const remove = (key, index) => {
    onChange(
      key,
      (config[key] || []).filter((_, rowIndex) => rowIndex !== index),
    );
  };

  const labels = {
    player_unavailability: {
      title: t("Horarios no disponibles", "Unavailable times"),
      description: t(
        "Bloqueos puntuales o recurrentes para un participante completo, incluyendo a todos sus jugadores.",
        "One-time or recurring blocks for a complete participant, including all of its players.",
      ),
      add: t("Agregar horario", "Add unavailable time"),
    },
    resource_preferences: {
      title: t("Canchas y clubes por categoría", "Courts and clubs by category"),
      description: t(
        "Preferencias u obligaciones de club y cancha para una categoría y fase.",
        "Club and court preferences or requirements for a category and phase.",
      ),
      add: t("Agregar regla de recurso", "Add resource rule"),
    },
    club_transfers: {
      title: t("Tiempo de traslado", "Transfer time"),
      description: t(
        "Tiempo entre clubes, adicional al descanso aplicable; entre torneos se exige solo el traslado.",
        "Time between clubs, in addition to applicable rest; between tournaments only transfer time is required.",
      ),
      add: t("Agregar traslado", "Add transfer time"),
    },
  };

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
            <BusinessIcon color="primary" />
            <Box>
              <Typography variant="h6">3. {t("Reglas de asignación", "Assignment rules")}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t(
                  "Revisa las reglas configuradas y abre el formulario solo cuando necesites agregar o editar una.",
                  "Review configured rules and open the form only when you need to add or edit one.",
                )}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1.5}>
            {Object.keys(RULES).map((key) => {
              const Icon = RULES[key].icon;
              const rows = config[key] || [];

              return (
                <RuleGroup
                  key={key}
                  icon={<Icon fontSize="small" />}
                  title={labels[key].title}
                  description={labels[key].description}
                  addLabel={labels[key].add}
                  rows={rows}
                  disabled={disabled}
                  emptyLabel={t("Sin reglas configuradas", "No rules configured")}
                  configuredLabel={t(`${rows.length} configuradas`, `${rows.length} configured`)}
                  editLabel={t("Editar", "Edit")}
                  removeLabel={t("Eliminar", "Remove")}
                  onAdd={() => openEditor(key)}
                  onEdit={(index) => openEditor(key, index)}
                  onRemove={(index) => remove(key, index)}
                  renderSummary={(row) =>
                    summarizeRule(key, row, { participants, tournaments, fields, clubs, intl, t })
                  }
                  groupByType={key === "player_unavailability"}
                  t={t}
                />
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <RuleDialog
        editor={editor}
        setEditor={setEditor}
        onClose={() => setEditor(null)}
        onApply={commitEditor}
        disabled={disabled}
        participants={participants}
        tournaments={tournaments}
        fields={fields}
        clubs={clubs}
        labels={labels}
        t={t}
      />
    </>
  );
}

function RuleGroup({
  icon,
  title,
  description,
  addLabel,
  rows,
  disabled,
  emptyLabel,
  configuredLabel,
  editLabel,
  removeLabel,
  onAdd,
  onEdit,
  onRemove,
  renderSummary,
  groupByType,
  t,
}) {
  const indexedRows = rows.map((row, index) => ({ row, index }));
  const sections = groupByType
    ? [
        { type: "once", title: t("Períodos puntuales", "One-time periods") },
        { type: "weekly", title: t("Cada semana", "Every week") },
        { type: "dates", title: t("Fechas elegidas", "Selected dates") },
      ].map((section) => ({
        ...section,
        entries: indexedRows.filter(({ row }) => (row.type || "once") === section.type),
      })).filter((section) => section.entries.length > 0)
    : [{ type: "all", entries: indexedRows }];

  const renderRow = ({ row, index }) => {
    const summary = renderSummary(row);
    return (
      <Stack key={index} direction="row" spacing={1} alignItems="center"
        sx={{ p: 1, bgcolor: "background.base", borderRadius: 1 }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{summary.primary}</Typography>
          {summary.secondary && <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {summary.secondary}
          </Typography>}
        </Box>
        {summary.badge && <Chip size="small" variant="outlined" label={summary.badge} />}
        <Tooltip title={editLabel}><span><IconButton size="small" disabled={disabled}
          aria-label={`${editLabel} ${title}`} onClick={() => onEdit(index)}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton></span></Tooltip>
        <Tooltip title={removeLabel}><span><IconButton size="small" disabled={disabled}
          aria-label={`${removeLabel} ${title}`} onClick={() => onRemove(index)}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton></span></Tooltip>
      </Stack>
    );
  };
  return (
    <Box
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, backgroundColor: "background.base" }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ color: "text.secondary", pt: 0.25 }}>{icon}</Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1">{title}</Typography>
              <Chip size="small" variant="outlined" label={configuredLabel} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {description}
            </Typography>
          </Box>
        </Stack>
        <Button variant="outlined" size="small" disabled={disabled} onClick={onAdd} sx={{ alignSelf: "flex-start" }}>
          {addLabel}
        </Button>
      </Stack>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, ml: { sm: 4.5 } }}>
          {emptyLabel}
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {sections.map((section) => <Stack key={section.type} spacing={0.75}>
            {groupByType && <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mt: 0.75 }}>
              {section.title} ({section.entries.length})
            </Typography>}
            {section.entries.map(renderRow)}
          </Stack>)}
        </Stack>
      )}
    </Box>
  );
}

function RuleDialog({
  editor,
  setEditor,
  onClose,
  onApply,
  disabled,
  participants,
  tournaments,
  fields,
  clubs,
  labels,
  t,
}) {
  if (!editor) return null;

  const change = (value) => setEditor((previous) => ({ ...previous, row: { ...previous.row, ...value } }));
  const replace = (row) => setEditor((previous) => ({ ...previous, row }));
  const editing = editor.index !== null;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        {editing ? t("Editar", "Edit") : t("Agregar", "Add")} {labels[editor.key].title.toLocaleLowerCase()}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {labels[editor.key].description}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          <RuleFields
            type={editor.key}
            row={editor.row}
            change={change}
            replace={replace}
            disabled={disabled}
            participants={participants}
            tournaments={tournaments}
            fields={fields}
            clubs={clubs}
            t={t}
          />
        </Stack>
        {editor.key === "player_unavailability" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t(
              editor.row.type === "weekly" || editor.row.type === "dates"
                ? "La restricción afecta al participante y a todos sus jugadores. Las horas se interpretan en la hora local de la sede del partido; 24:00 significa hasta el fin del día."
                : "La restricción afecta al participante y a todos sus jugadores. Si termina el mismo día, solo necesitas indicar la hora de fin.",
              editor.row.type === "weekly" || editor.row.type === "dates"
                ? "The block affects the participant and all of its players. Times use the match venue's local time; 24:00 means the end of the day."
                : "The block affects the participant and all of its players. For a same-day block, only an end time is needed.",
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancelar", "Cancel")}</Button>
        <Button variant="contained" disabled={disabled} onClick={onApply}>
          {t("Aplicar regla", "Apply rule")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RuleFields({ type, row, change, replace, disabled, participants, tournaments, fields, clubs, t }) {
  const [newDate, setNewDate] = useState("");
  const [showEndDate, setShowEndDate] = useState(() => {
    const start = toLocalDateTime(row.start);
    const end = toLocalDateTime(row.end);
    return Boolean(start && end && start.slice(0, 10) !== end.slice(0, 10));
  });
  const select = (label, value, options, onChange, multiple = false) => (
    <TextField
      select
      size="small"
      label={label}
      value={value ?? (multiple ? [] : "")}
      disabled={disabled}
      sx={{ minWidth: 210, flexGrow: 1 }}
      slotProps={{ select: { multiple } }}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {option.name || `#${option.id}`}
        </MenuItem>
      ))}
    </TextField>
  );

  if (type === "player_unavailability") {
    const mode = row.type || "once";
    const startLocal = toLocalDateTime(row.start);
    const endLocal = toLocalDateTime(row.end);
    const weekdayNames = [
      t("Lun", "Mon"),
      t("Mar", "Tue"),
      t("Mié", "Wed"),
      t("Jue", "Thu"),
      t("Vie", "Fri"),
      t("Sáb", "Sat"),
      t("Dom", "Sun"),
    ];
    const selectedParticipant =
      participants.find((participant) => String(participant.id) === String(row.participant_id)) || null;
    return (
      <>
        <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ width: "100%" }}>
          <Autocomplete
            fullWidth
            options={participants}
            value={selectedParticipant}
            disabled={disabled}
            autoHighlight
            getOptionLabel={(option) =>
              option.tournament_name ? `${option.name} · ${option.tournament_name}` : option.name || `#${option.id}`
            }
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            onChange={(_, participant) => change({ participant_id: participant?.id ?? "", person_key: undefined })}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("Participante", "Participant")}
                placeholder={t("Buscar por nombre…", "Search by name…")}
              />
            )}
          />
          {select(
            t("Tipo de bloqueo", "Block type"),
            mode,
            [
              { id: "once", name: t("Período puntual", "One-time period") },
              { id: "weekly", name: t("Cada semana", "Every week") },
              { id: "dates", name: t("Fechas elegidas", "Selected dates") },
            ],
            (nextMode) =>
              replace({
                participant_id: row.participant_id,
                type: nextMode,
                ...(nextMode === "once"
                  ? { start: "", end: "" }
                  : {
                      start_time: "00:00",
                      end_time: "24:00",
                      ...(nextMode === "weekly" ? { weekdays: [] } : { dates: [] }),
                    }),
              }),
          )}
        </Stack>
        {mode === "once" && (
          <Stack direction="row" flexWrap="wrap" sx={{ width: "100%", gap: 1 }}>
            <TextField size="small" type="datetime-local" label={t("Desde", "From")}
              slotProps={{ inputLabel: { shrink: true } }} value={startLocal} disabled={disabled}
              sx={{ minWidth: 220, flexGrow: 1 }} onChange={(event) => {
                const local = event.target.value;
                const endTime = endLocal.slice(11, 16);
                const endDate = showEndDate ? endLocal.slice(0, 10) || local.slice(0, 10) : local.slice(0, 10);
                change({ start: toIsoDateTime(local), end: endTime && endDate
                  ? toIsoDateTime(`${endDate}T${endTime}`) : "" });
              }} />
            <TextField size="small" type="time" label={t("Hasta (hora)", "Until (time)")}
              slotProps={{ inputLabel: { shrink: true } }} value={endLocal.slice(11, 16)} disabled={disabled}
              sx={{ minWidth: 180, flexGrow: 1 }} onChange={(event) => {
                const date = showEndDate ? endLocal.slice(0, 10) || startLocal.slice(0, 10) : startLocal.slice(0, 10);
                change({ end: date && event.target.value ? toIsoDateTime(`${date}T${event.target.value}`) : "" });
              }} />
            <FormControlLabel control={<Checkbox checked={showEndDate} disabled={disabled}
              onChange={(event) => {
                setShowEndDate(event.target.checked);
                if (!event.target.checked && startLocal && endLocal) {
                  change({ end: toIsoDateTime(`${startLocal.slice(0, 10)}T${endLocal.slice(11, 16)}`) });
                }
              }} />} label={t("Termina otro día", "Ends on another day")} />
            {showEndDate && <TextField size="small" type="date" label={t("Fecha de fin", "End date")}
              slotProps={{ inputLabel: { shrink: true } }} value={endLocal.slice(0, 10) || startLocal.slice(0, 10)}
              disabled={disabled} sx={{ minWidth: 180 }} onChange={(event) => {
                const time = endLocal.slice(11, 16);
                change({ end: event.target.value && time ? toIsoDateTime(`${event.target.value}T${time}`) : "" });
              }} />}
          </Stack>
        )}
        {mode === "weekly" && (
          <Box sx={{ width: "100%" }}>
            <Typography variant="body2" sx={{ mb: 0.75 }}>
              {t("Días de la semana", "Days of the week")}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {weekdayNames.map((name, index) => {
                const day = index + 1;
                const selected = (row.weekdays || []).includes(day);
                return (
                  <Chip
                    key={day}
                    clickable
                    disabled={disabled}
                    label={name}
                    color={selected ? "primary" : "default"}
                    variant={selected ? "filled" : "outlined"}
                    onClick={() =>
                      change({
                        weekdays: selected
                          ? row.weekdays.filter((value) => value !== day)
                          : [...(row.weekdays || []), day].sort(),
                      })
                    }
                  />
                );
              })}
            </Stack>
          </Box>
        )}
        {mode === "dates" && (
          <Box sx={{ width: "100%" }}>
            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
              <TextField
                size="small"
                type="date"
                label={t("Fecha", "Date")}
                value={newDate}
                disabled={disabled}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(event) => setNewDate(event.target.value)}
              />
              <Button
                variant="outlined"
                disabled={disabled || !newDate || (row.dates || []).includes(newDate)}
                onClick={() => {
                  change({ dates: [...(row.dates || []), newDate].sort() });
                  setNewDate("");
                }}
              >
                {t("Agregar fecha", "Add date")}
              </Button>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
              {(row.dates || []).map((date) => (
                <Chip
                  key={date}
                  label={date}
                  disabled={disabled}
                  onDelete={() => change({ dates: row.dates.filter((value) => value !== date) })}
                />
              ))}
            </Stack>
          </Box>
        )}
        {mode !== "once" && (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ width: "100%", mt: 1 }}>
            <TextField
              size="small"
              type="time"
              label={t("No disponible desde", "Unavailable from")}
              value={row.start_time || ""}
              disabled={disabled}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(event) => change({ start_time: event.target.value })}
              sx={{ minWidth: 190, flexGrow: 1 }}
            />
            <TextField
              size="small"
              type="time"
              label={t("Hasta", "Until")}
              value={row.end_time === "24:00" ? "" : row.end_time || ""}
              disabled={disabled || row.end_time === "24:00"}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(event) => change({ end_time: event.target.value })}
              sx={{ minWidth: 190, flexGrow: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={row.end_time === "24:00"}
                  disabled={disabled}
                  onChange={(event) => change({ end_time: event.target.checked ? "24:00" : "23:59" })}
                />
              }
              label={t("Hasta fin del día", "Until end of day")}
            />
          </Stack>
        )}
      </>
    );
  }

  if (type === "resource_preferences") {
    return (
      <>
        {select(t("Torneo / categoría", "Tournament / category"), row.tournament_id, tournaments, (tournament_id) =>
          change({ tournament_id }),
        )}
        {select(
          t("Fase", "Phase"),
          row.phase,
          [
            { id: "all", name: t("Todas", "All") },
            { id: "final", name: "Final" },
            { id: "semifinal", name: t("Semifinal", "Semifinal") },
          ],
          (phase) => change({ phase }),
        )}
        {select(
          t("Modo", "Mode"),
          row.mode,
          [
            { id: "preferred", name: t("Preferido", "Preferred") },
            { id: "required", name: t("Obligatorio", "Required") },
          ],
          (mode) => change({ mode }),
        )}
        {select(
          t("Clubes", "Clubs"),
          row.establishment_ids,
          clubs,
          (establishment_ids) => change({ establishment_ids }),
          true,
        )}
        {select(
          t("Canchas", "Courts"),
          row.field_ids,
          fields.map((field) => ({ ...field, name: `${field.establishment_name} · ${field.name}` })),
          (field_ids) => change({ field_ids }),
          true,
        )}
      </>
    );
  }

  return (
    <>
      {select(t("Club A", "Club A"), row.from_club_id, clubs, (from_club_id) => change({ from_club_id }))}
      {select(t("Club B", "Club B"), row.to_club_id, clubs, (to_club_id) => change({ to_club_id }))}
      <TextField
        size="small"
        type="number"
        label={t("Minutos adicionales", "Additional minutes")}
        value={row.minutes}
        disabled={disabled}
        sx={{ minWidth: 210, flexGrow: 1 }}
        onChange={(event) => change({ minutes: Number(event.target.value) })}
      />
    </>
  );
}

function summarizeRule(type, row, { participants, tournaments, fields, clubs, intl, t }) {
  const findName = (options, id) =>
    options.find((option) => String(option.id) === String(id))?.name || (id ? `#${id}` : "—");

  if (type === "player_unavailability") {
    const participant = participants.find((option) => String(option.id) === String(row.participant_id));
    const weekdays = [
      t("Lun", "Mon"),
      t("Mar", "Tue"),
      t("Mié", "Wed"),
      t("Jue", "Thu"),
      t("Vie", "Fri"),
      t("Sáb", "Sat"),
      t("Dom", "Sun"),
    ];
    const recurrence =
      row.type === "weekly"
        ? (row.weekdays || []).map((day) => weekdays[day - 1]).join(", ")
        : (row.dates || []).join(", ");
    return {
      primary: row.participant_id
        ? participant?.tournament_name
          ? `${participant.name} · ${participant.tournament_name}`
          : findName(participants, row.participant_id)
        : t("Restricción anterior por persona", "Previous person-based restriction"),
      secondary:
        row.type === "weekly" || row.type === "dates"
          ? `${recurrence || "—"} · ${row.start_time}–${row.end_time}`
          : `${formatDateTime(row.start, intl)} – ${formatDateTime(row.end, intl)}`,
    };
  }

  if (type === "resource_preferences") {
    const resources = [
      ...(row.establishment_ids || []).map((id) => findName(clubs, id)),
      ...(row.field_ids || []).map((id) => findName(fields, id)),
    ];
    const phases = {
      all: t("Todas las fases", "All phases"),
      final: "Final",
      semifinal: t("Semifinal", "Semifinal"),
    };
    return {
      primary: `${findName(tournaments, row.tournament_id)} · ${phases[row.phase] || row.phase}`,
      secondary: resources.length ? resources.join(", ") : t("Sin recursos seleccionados", "No resources selected"),
      badge: row.mode === "required" ? t("Obligatorio", "Required") : t("Preferido", "Preferred"),
    };
  }

  return {
    primary: `${findName(clubs, row.from_club_id)} ↔ ${findName(clubs, row.to_club_id)}`,
    secondary: t(`${row.minutes} minutos adicionales`, `${row.minutes} additional minutes`),
  };
}

function formatDateTime(value, intl) {
  if (!value || Number.isNaN(new Date(value).getTime())) return "—";
  return intl.formatDate(value, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toLocalDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function toIsoDateTime(value) {
  return value ? new Date(value).toISOString().replace(".000Z", "+00:00") : "";
}

function cloneRule(rule) {
  return {
    ...rule,
    ...(rule.field_ids ? { field_ids: [...rule.field_ids] } : {}),
    ...(rule.establishment_ids ? { establishment_ids: [...rule.establishment_ids] } : {}),
    ...(rule.weekdays ? { weekdays: [...rule.weekdays] } : {}),
    ...(rule.dates ? { dates: [...rule.dates] } : {}),
  };
}
